/**
 * PDF generation utilities for assessment results
 */
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { createRgbProcessedClone, oklchToRgb, oklabToRgb, parseOklabString, parseColorMix, checkForRemainingOklchColors } from './color-utils';
import { Assessment } from '../types/assessment';

/**
 * Robust OKLCH -> RGB parser supporting percentage lightness and optional alpha
 */
export const parseOklchToRgb = (oklchStr: string): string => {
  try {
    const m = oklchStr.match(/oklch\(\s*([\d.]+)\s*(%)?\s+([\d.]+)\s*(%)?\s+([\d.]+|none)(?:\s*\/\s*([\d.]+)\s*(%)?)?\s*\)/i);
    if (!m) return oklchStr;
    const l = parseFloat(m[1]);
    const lIsPct = !!m[2];
    const c = parseFloat(m[3]);
    const cIsPct = !!m[4];
    const h = m[5] && m[5].toLowerCase() !== 'none' ? parseFloat(m[5]) : 0;
    const a = m[6] ? parseFloat(m[6]) : undefined;
    const aIsPct = !!m[7];
    const lNorm = lIsPct ? l / 100 : l; // normalize percentage to 0..1
    const cNorm = cIsPct ? c / 100 : c; // rarely percent; handle just in case
    const rgb = oklchToRgb(lNorm, cNorm, h);
    if (a !== undefined) {
      const alpha = aIsPct ? a / 100 : a;
      // rgb(...) -> extract numbers to build rgba
      const m2 = rgb.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
      if (m2) {
        return `rgba(${m2[1]}, ${m2[2]}, ${m2[3]}, ${Math.max(0, Math.min(1, alpha))})`;
      }
    }
    return rgb;
  } catch {
    return 'rgb(0, 0, 0)';
  }
};

/**
 * Robust OKLab -> RGB parser supporting percentage values and optional alpha
 */
export const parseOklabToRgb = (oklabStr: string): string => {
  try {
    const m = oklabStr.match(/oklab\(\s*([\d.]+)\s*(%)?\s+(-?[\d.]+)\s*(%)?\s+(-?[\d.]+)(?:\s*\/\s*([\d.]+)\s*(%)?)?\s*\)/i);
    if (!m) {
      console.warn('[pdf-utils] Failed to parse oklab:', oklabStr);
      return 'rgb(128, 128, 128)';
    }
    let l = parseFloat(m[1]);
    const lIsPct = !!m[2];
    let a = parseFloat(m[3]);
    const aIsPct = !!m[4];
    let b = parseFloat(m[5]);
    const alpha = m[6] ? parseFloat(m[6]) : undefined;
    const alphaIsPct = !!m[7];
    
    // Normalize percentages
    if (lIsPct) l = l / 100;
    if (aIsPct) a = a / 100 * 0.4;
    // b doesn't have a % flag in the regex but handle it anyway
    
    const rgb = oklabToRgb(l, a, b);
    if (alpha !== undefined) {
      const alphaNorm = alphaIsPct ? alpha / 100 : alpha;
      const m2 = rgb.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
      if (m2) {
        return `rgba(${m2[1]}, ${m2[2]}, ${m2[3]}, ${Math.max(0, Math.min(1, alphaNorm))})`;
      }
    }
    return rgb;
  } catch (e) {
    console.warn('[pdf-utils] Error parsing oklab:', oklabStr, e);
    return 'rgb(128, 128, 128)';
  }
};

/**
 * Parse any ok* color format (oklch or oklab)
 */
export const parseOkColorToRgb = (colorStr: string): string => {
  if (colorStr.toLowerCase().startsWith('oklab')) {
    return parseOklabToRgb(colorStr);
  } else if (colorStr.toLowerCase().startsWith('oklch')) {
    return parseOklchToRgb(colorStr);
  }
  return colorStr;
};

/**
 * Get element identifier for logging
 */
const getElementId = (el: Element): string => {
  const tag = el.tagName?.toLowerCase() || 'unknown';
  const id = el.id ? `#${el.id}` : '';
  const classes = el.className && typeof el.className === 'string' 
    ? '.' + el.className.split(' ').filter(Boolean).slice(0, 2).join('.') 
    : '';
  return `${tag}${id}${classes}`;
};

/**
 * Sanitize a single CSS value - handles color-mix, oklch, oklab
 */
const sanitizeCssValue = (val: string): string => {
  let result = val;
  // Handle color-mix first (it may contain oklch/oklab)
  if (result.includes('color-mix')) {
    result = result.replace(/color-mix\([^)]+\)/gi, (m) => parseColorMix(m));
  }
  if (result.includes('oklch')) {
    result = result.replace(/oklch\([^)]+\)/gi, (m) => parseOklchToRgb(m));
  }
  if (result.includes('oklab')) {
    result = result.replace(/oklab\([^)]+\)/gi, (m) => parseOklabToRgb(m));
  }
  return result;
};

/**
 * Deep sanitize OKLCH/OKLab/color-mix occurrences across computed styles, attributes, and SVGs
 */
export const sanitizeColors = (root: HTMLElement) => {
  const sanitizeEl = (el: Element) => {
    // Replace in style attribute
    const styleAttr = el.getAttribute('style');
    if (styleAttr && (styleAttr.includes('oklch') || styleAttr.includes('oklab') || styleAttr.includes('color-mix'))) {
      const newStyle = sanitizeCssValue(styleAttr);
      if (newStyle !== styleAttr) {
        el.setAttribute('style', newStyle);
      }
    }

    // Replace in attributes for SVG
    const svgColorAttrs = ['fill', 'stroke', 'stop-color', 'flood-color', 'lighting-color'];
    svgColorAttrs.forEach((attr) => {
      const v = el.getAttribute(attr);
      if (v && (v.includes('oklch') || v.includes('oklab') || v.includes('color-mix'))) {
        const newV = sanitizeCssValue(v);
        if (newV !== v) {
          el.setAttribute(attr, newV);
        }
      }
    });

    // Computed styles: set inline override for any property containing problematic colors
    const cs = getComputedStyle(el as Element as HTMLElement);
    for (let i = 0; i < cs.length; i++) {
      const prop = cs[i];
      if (!prop) continue;
      const val = cs.getPropertyValue(prop);
      if (val && (val.includes('oklch') || val.includes('oklab') || val.includes('color-mix'))) {
        const newVal = sanitizeCssValue(val);
        if (newVal !== val) {
          (el as HTMLElement).style.setProperty(prop, newVal, 'important');
        }
      }
    }
  };

  sanitizeEl(root);
  root.querySelectorAll('*').forEach((n) => sanitizeEl(n as Element));

  // Final fallback: string-replace any lingering problematic colors in subtree HTML
  try {
    let html = root.innerHTML;
    if (html && (html.includes('oklch') || html.includes('oklab') || html.includes('color-mix'))) {
      html = sanitizeCssValue(html);
      root.innerHTML = html;
    }
  } catch {}
};

/**
 * Generate a PDF from an HTML element
 * This function can be used both from the UI component and programmatically
 * 
 * @param sourceEl The HTML element to convert to PDF
 * @param filename The name for the saved PDF file
 * @param options Additional options (download, landscape)
 * @returns The jsPDF document object and optionally triggers a download
 */
export const generatePdf = async (
  sourceEl: HTMLElement, 
  filename: string = 'essential-eight-assessment.pdf',
  options: {
    download?: boolean, 
    landscape?: boolean,
    returnBlob?: boolean,
    scale?: number
  } = {}
): Promise<jsPDF | Blob> => {
  const { 
    download = true, 
    landscape = true,
    returnBlob = false,
    scale = 2
  } = options;

  // Create an offscreen container and clone of the content to render
  const offscreen = document.createElement('div');
  offscreen.style.position = 'fixed';
  offscreen.style.left = '-10000px';
  offscreen.style.top = '0';
  offscreen.style.width = `${sourceEl.clientWidth}px`;
  offscreen.style.background = '#ffffff';

  // Create an aggressively sanitized clone using our utilities
  const clone = createRgbProcessedClone(sourceEl);
  clone.setAttribute('data-pdf-root', '1');
  offscreen.appendChild(clone);
  document.body.appendChild(offscreen);

  // Sanitize colors after the clone is attached so computed styles resolve
  sanitizeColors(clone);

  // Small delay to ensure styles/images settle
  await new Promise((r) => setTimeout(r, 30));

  // Debug: surface any remaining problematic colors before rendering
  if (checkForRemainingOklchColors(clone)) {
    console.warn('[PDF Export] Remaining problematic colors detected after sanitization. Attempting final fallback replacement.');
    clone.innerHTML = sanitizeCssValue(clone.innerHTML);
  }

  // Render to canvas
  const canvas = await html2canvas(clone, {
    scale,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
    onclone: (doc) => {
      const root = doc.querySelector('[data-pdf-root="1"]') as HTMLElement | null;
      if (root) {
        try {
          // Run sanitization again inside html2canvas clone context
          const localSanitize = (rt: HTMLElement) => {
            const walk = (el: Element) => {
              // Handle style attribute
              const styleAttr = el.getAttribute('style');
              if (styleAttr && (styleAttr.includes('oklch') || styleAttr.includes('oklab') || styleAttr.includes('color-mix'))) {
                const newStyle = sanitizeCssValue(styleAttr);
                if (newStyle !== styleAttr) {
                  el.setAttribute('style', newStyle);
                }
              }
              
              // Handle SVG attributes
              const svgAttrs = ['fill','stroke','stop-color','flood-color','lighting-color'];
              svgAttrs.forEach(a => {
                const v = el.getAttribute(a);
                if (v && (v.includes('oklch') || v.includes('oklab') || v.includes('color-mix'))) {
                  const newV = sanitizeCssValue(v);
                  if (newV !== v) {
                    el.setAttribute(a, newV);
                  }
                }
              });
              
              // Handle computed styles
              if (el instanceof HTMLElement) {
                const cs = doc.defaultView?.getComputedStyle(el);
                if (cs) {
                  for (let i = 0; i < cs.length; i++) {
                    const p = cs[i];
                    const val = cs.getPropertyValue(p);
                    if (val && (val.includes('oklch') || val.includes('oklab') || val.includes('color-mix'))) {
                      const newVal = sanitizeCssValue(val);
                      if (newVal !== val) {
                        el.style.setProperty(p, newVal, 'important');
                      }
                    }
                  }
                }
              }
            };
            walk(rt);
            rt.querySelectorAll('*').forEach(n => walk(n as Element));
            try {
              const html = rt.innerHTML;
              if (html && (html.includes('oklch') || html.includes('oklab') || html.includes('color-mix'))) {
                rt.innerHTML = sanitizeCssValue(html);
              }
            } catch {}
          };
          localSanitize(root);
        } catch {}
      }
    },
  });

  // Clean up offscreen DOM
  document.body.removeChild(offscreen);

  // Build single-page PDF that CONTAINS the image (fit by width or height)
  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF(landscape ? 'l' : 'p', 'mm', 'a4');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  // Preserve aspect ratio and fit entirely within page
  const imgRatio = canvas.width / canvas.height;
  let drawW = pdfWidth;
  let drawH = drawW / imgRatio;
  if (drawH > pdfHeight) {
    drawH = pdfHeight;
    drawW = drawH * imgRatio;
  }

  // Center on the page
  const x = (pdfWidth - drawW) / 2;
  const y = (pdfHeight - drawH) / 2;

  pdf.addImage(imgData, 'PNG', x, y, drawW, drawH);

  // Either download the PDF or return it as needed
  if (download) {
    pdf.save(filename);
  }
  
  // Return blob if requested (for uploading to HubSpot)
  if (returnBlob || !download) {
    return pdf.output('blob');
  }

  return pdf;
};

/**
 * Convert a PDF to base64 for API transmission
 * @param pdfBlob PDF blob to convert
 * @returns Promise resolving to base64 string
 */
export const pdfToBase64 = async (pdfBlob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      // Remove the prefix (data:application/pdf;base64,)
      const base64 = base64String.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(pdfBlob);
  });
};

/**
 * Generate a PDF from assessment data without requiring DOM elements
 * This is useful for programmatic PDF generation (e.g., server-side or background tasks)
 * 
 * @param assessment The assessment data object
 * @param filename The name for the saved PDF file
 * @returns The jsPDF document object as a Blob
 */
export const generateAssessmentPdf = async (
  assessment: Assessment,
  filename: string = 'essential-eight-assessment.pdf'
): Promise<Blob> => {
  // Create a PDF document in landscape orientation
  const pdf = new jsPDF('l', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  
  // Add title
  pdf.setFontSize(18);
  pdf.text('Essential Eight Assessment Results', pageWidth / 2, margin + 10, { align: 'center' });
  
  // Add version information
  pdf.setFontSize(10);
  pdf.text(`Version: ${assessment.questionnaireVersion?.version || '1.0'}`, pageWidth / 2, margin + 18, { align: 'center' });
  
  // Add timestamp
  const timestamp = new Date().toISOString().split('T')[0];
  pdf.text(`Generated: ${timestamp}`, pageWidth / 2, margin + 25, { align: 'center' });
  
  // Calculate overall maturity
  const maturityLevels = Object.values(assessment.maturityLevels || {});
  const overallMaturity = maturityLevels.length > 0 ? Math.min(...maturityLevels as number[]) : 0;
  
  pdf.setFontSize(14);
  pdf.text(`Overall Maturity Level: ${overallMaturity}`, margin, margin + 40);
  
  // Add table of maturity levels by category
  const tableData = Object.entries(assessment.maturityLevels || {}).map(([category, level]) => [
    category.charAt(0).toUpperCase() + category.slice(1).replace(/-/g, ' '), // Format category name
    level.toString()
  ]);
  
  // Add table header
  pdf.setFontSize(12);
  pdf.text('Category', margin, margin + 55);
  pdf.text('Maturity Level', margin + 100, margin + 55);
  
  // Add horizontal line
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, margin + 58, pageWidth - margin, margin + 58);
  
  // Add table rows
  let y = margin + 65;
  tableData.forEach(([category, level], index) => {
    // Add alternating background for rows
    if (index % 2 === 0) {
      pdf.setFillColor(245, 245, 245);
      pdf.rect(margin, y - 5, pageWidth - (margin * 2), 10, 'F');
    }
    
    pdf.text(category, margin, y);
    
    // Set color for maturity level
    const levelNum = parseInt(level);
    if (levelNum === 0) pdf.setTextColor(240, 50, 50); // Red
    else if (levelNum === 1) pdf.setTextColor(240, 140, 0); // Orange
    else if (levelNum === 2) pdf.setTextColor(240, 200, 0); // Yellow
    else if (levelNum === 3) pdf.setTextColor(0, 180, 0); // Green
    
    pdf.text(level, margin + 100, y);
    pdf.setTextColor(0, 0, 0); // Reset to black
    
    y += 10;
    
    // Check if we need a new page
    if (y > pageHeight - margin) {
      pdf.addPage();
      y = margin + 20;
    }
  });
  
  // Add summary section
  pdf.addPage();
  pdf.setFontSize(16);
  pdf.text('Assessment Summary', margin, margin + 10);
  
  // Format strengths and priorities
  const sortedCategories = [...tableData]
    .sort((a, b) => parseInt(b[1]) - parseInt(a[1]));
  
  const strengths = sortedCategories.filter(([_, level]) => parseInt(level) >= 2).slice(0, 3);
  const priorities = sortedCategories.filter(([_, level]) => parseInt(level) < 2).slice(0, 3);
  
  // Add strengths
  y = margin + 30;
  pdf.setFontSize(14);
  pdf.text('Strengths:', margin, y);
  y += 10;
  
  if (strengths.length > 0) {
    strengths.forEach(([category, level]) => {
      pdf.setFontSize(12);
      pdf.text(`• ${category} (Level ${level})`, margin + 5, y);
      y += 8;
    });
  } else {
    pdf.setFontSize(12);
    pdf.text('No strengths identified yet', margin + 5, y);
    y += 8;
  }
  
  // Add priorities
  y += 10;
  pdf.setFontSize(14);
  pdf.text('Priorities:', margin, y);
  y += 10;
  
  if (priorities.length > 0) {
    priorities.forEach(([category, level]) => {
      pdf.setFontSize(12);
      pdf.text(`• ${category} (Level ${level})`, margin + 5, y);
      y += 8;
    });
  } else {
    pdf.setFontSize(12);
    pdf.text('No priorities identified yet', margin + 5, y);
    y += 8;
  }
  
  // Add footer with disclaimer
  const footerText = 'This assessment was generated automatically and provides a high-level overview of your Essential Eight maturity.';
  pdf.setFontSize(8);
  pdf.text(footerText, pageWidth / 2, pageHeight - 10, { align: 'center' });
  
  // Return the PDF as a blob
  return pdf.output('blob');
};
